import type { AdminClauseOverride } from '@/apis/extended/admin/adminWorkspaceApi';
import type { Proposal } from '@/apis/extended/proposalApi';
import type { ContractClauseComparison } from '@/components/Admin/ContractComparisonTable';
import {
  extractComprehensiveContractClausesWithIds,
  type ComprehensiveClauseCategory,
  type ComprehensiveClauseData,
} from '@/utils/comprehensiveClauseMapping';
import type { Contract } from '@/types/ContractType';

function ts(p: Proposal): number {
  const d = p.updatedAt || p.createdAt;
  return d ? new Date(d).getTime() : 0;
}

function proposalsForClause(
  proposals: Proposal[],
  clauseId: string
): Proposal[] {
  return proposals
    .filter((p) => p.clauseId === clauseId)
    .sort((a, b) => ts(b) - ts(a));
}

function latestByRole(
  proposals: Proposal[],
  clauseId: string,
  role: Proposal['proposedBy']
): Proposal | undefined {
  return proposalsForClause(proposals, clauseId).find(
    (p) => p.proposedBy === role
  );
}

function mapTableCategory(catId: string): ContractClauseComparison['category'] {
  if (catId === 'price') return 'offer';
  if (catId === 'timeline') return 'timeline';
  if (catId === 'contingency' || catId === 'inspection') return 'contingency';
  if (catId === 'occupancy') return 'timeline';
  if (catId === 'property') return 'property';
  if (catId === 'legal') return 'other';
  return 'other';
}

/** Comparable form of a clause value: "$530,000" and 530000 are the same. */
export function normalizeClauseValue(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'number') return String(v);
  const str = String(v).trim();
  const numeric = str.replace(/[$,\s]/g, '');
  if (numeric !== '' && Number.isFinite(Number(numeric))) {
    return String(Number(numeric));
  }
  return str.toLowerCase();
}

/** True when the parties' latest positions on a clause differ. */
export function partiesDisagree(...inputs: unknown[]): boolean {
  const vals = inputs.map(normalizeClauseValue).filter((v) => v !== '');
  return new Set(vals).size > 1;
}

// The backend has more accepted states than the Proposal type lists.
const ACCEPTED_STATUSES = new Set<string>([
  'accepted',
  'accepted_pending_ratification',
  'accepted_and_applied',
  'accepted_with_conditions',
]);

function isAccepted(p: Proposal | undefined): boolean {
  return !!p && ACCEPTED_STATUSES.has(String(p.status));
}

function mapRowStatus(
  clause: ComprehensiveClauseData,
  clauseProposals: Proposal[]
): ContractClauseComparison['status'] {
  const latest = clauseProposals[0];
  if (latest) {
    const s = latest.status;
    if (isAccepted(latest)) return 'agreed';
    if (s === 'rejected' || s === 'expired') return 'disputed';
    if (s === 'countered') return 'modified';
    if (s === 'pending') return 'pending';
  }
  const n = clause.negotiationStatus;
  if (n === 'rejected') return 'disputed';
  if (n === 'countered') return 'modified';
  return 'agreed';
}

function mapLastUpdatedBy(
  clauseProposals: Proposal[]
): ContractClauseComparison['lastUpdatedBy'] {
  const latest = clauseProposals[0];
  if (latest?.proposedBy === 'buyer') return 'buyer';
  if (latest?.proposedBy === 'seller') return 'seller';
  if (latest?.proposedBy === 'agent') return 'agent';
  return 'agent';
}

function buildNegotiationHistory(
  clauseProposals: Proposal[]
): ContractClauseComparison['negotiationHistory'] {
  return [...clauseProposals]
    .sort((a, b) => ts(a) - ts(b))
    .map((p) => ({
      party: p.proposedBy,
      value: p.proposedValue,
      timestamp: new Date(p.createdAt),
      notes: [p.message, p.status && `Status: ${p.status}`]
        .filter(Boolean)
        .join(' · '),
    }));
}

function toComparisonRow(
  category: ComprehensiveClauseCategory,
  clause: ComprehensiveClauseData,
  proposals: Proposal[],
  overrides: AdminClauseOverride[],
  isRatified: boolean
): ContractClauseComparison {
  const clauseProposals = proposalsForClause(proposals, clause.id);
  const lastBuyer = latestByRole(proposals, clause.id, 'buyer');
  const lastSeller = latestByRole(proposals, clause.id, 'seller');
  const lastAgent = latestByRole(proposals, clause.id, 'agent');

  const effective = clause.value;

  // An accepted proposal is every party's position from then on: the buyer's
  // older $530k ask doesn't stand once they accepted the seller's $540k.
  const accepted = clauseProposals.find(isAccepted);
  const positionOf = (own: Proposal | undefined) => {
    if (isRatified) return effective;
    if (accepted && (!own || ts(own) <= ts(accepted))) {
      return accepted.proposedValue;
    }
    return own?.proposedValue ?? effective;
  };

  const buyerInput = positionOf(lastBuyer);
  const sellerInput = positionOf(lastSeller);
  const agentInput = positionOf(lastAgent);

  const notesParts = [
    clause.description,
    clause.source && `Source: ${clause.source}`,
  ].filter(Boolean);

  const latest = clauseProposals[0];
  if (latest?.message) notesParts.push(`Latest note: ${latest.message}`);

  // A ratified contract's terms are agreed by definition (both signed).
  let status = isRatified ? 'agreed' : mapRowStatus(clause, clauseProposals);
  let statusNote: string | undefined;
  // "Agreed" only when the positions shown in this row match — a seller at
  // $540k next to a buyer and agent at $530k is not agreed.
  if (
    status === 'agreed' &&
    partiesDisagree(sellerInput, buyerInput, agentInput)
  ) {
    status = 'disputed';
    statusNote = 'The parties’ latest positions differ';
  }
  // An admin decision newer than the last proposal wins, with its reason.
  const override = overrides
    .filter((o) => o.clauseId === clause.id)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];
  if (
    override &&
    new Date(override.at).getTime() >= (latest ? ts(latest) : 0)
  ) {
    status = override.action === 'agree' ? 'agreed' : 'pending';
    statusNote = `${
      override.action === 'agree' ? 'Marked agreed' : 'Reopened'
    } by ${override.adminName}: ${override.reason}`;
  }

  return {
    clauseId: clause.id,
    clauseName: clause.title,
    category: mapTableCategory(category.id),
    sellerInput,
    agentInput,
    buyerInput,
    adminNotes: notesParts.join(' · '),
    status,
    statusNote,
    lastUpdatedBy: mapLastUpdatedBy(clauseProposals),
    lastUpdatedAt: latest
      ? new Date(latest.updatedAt || latest.createdAt)
      : new Date(),
    negotiationHistory: buildNegotiationHistory(clauseProposals),
  };
}

/**
 * Full clause grid for admin property dashboard: same Clause Library as negotiation flows,
 * with per-party columns derived from proposals where available.
 */
/** Populated `buyer` user from contract GET; not on base `Contract` type. */
type ContractWithPopulatedBuyer = Contract & {
  buyer?: {
    loanInfo?: {
      monthlyIncome?: number;
      creditScore?: number;
      monthlyDebt?: number;
    };
  };
};

function appendBuyerFinancialSnapshot(
  contract: Contract,
  rows: ContractClauseComparison[]
) {
  const { buyer } = contract as ContractWithPopulatedBuyer;
  const income = buyer?.loanInfo?.monthlyIncome;
  const credit = buyer?.loanInfo?.creditScore;
  const debt = buyer?.loanInfo?.monthlyDebt;
  const baseDate = new Date(
    (contract.updatedAt || contract.createdAt) as string
  );

  const push = (
    clauseName: string,
    val: string | number | null | undefined
  ) => {
    if (val == null || val === '') return;
    const v = val as string | number;
    rows.push({
      clauseName,
      category: 'other',
      sellerInput: v,
      agentInput: v,
      buyerInput: v,
      adminNotes: 'Buyer qualification (URLA)',
      status: 'agreed',
      lastUpdatedBy: 'buyer',
      lastUpdatedAt: baseDate,
      negotiationHistory: [],
    });
  };

  push('Buyer Monthly Income', income);
  push('Buyer Credit Score', credit);
  push('Buyer Monthly Debt', debt);
}

export function buildAdminContractComparisonRows(
  contract: Contract,
  contractId: string,
  proposals: Proposal[],
  overrides: AdminClauseOverride[] = []
): ContractClauseComparison[] {
  if (!contract || !contractId) return [];

  const merged: Proposal[] = Array.isArray(proposals) ? proposals : [];
  const categories = extractComprehensiveContractClausesWithIds(
    contract,
    contractId
  );

  const isRatified =
    Boolean(contract.isRatified) || contract.contractStatus === 'ratified';
  const rows: ContractClauseComparison[] = [];
  for (const category of categories) {
    for (const clause of category.clauses) {
      rows.push(
        toComparisonRow(category, clause, merged, overrides, isRatified)
      );
    }
  }
  appendBuyerFinancialSnapshot(contract, rows);
  return rows;
}
